import { useState, useCallback } from "react";
import { isAxiosError } from "axios";
import instance from "../utils/AxiosConfiguration";

/**
 * BMC-Fassung (T-29): trägt zusätzlich den HTTP-Status und den Response-Body
 * eines Axios-Fehlers nach — der 404/410-Kontrakt aus T-28 liefert im Body
 * `{ kind: 'notFound' | 'gone', context: {...}, template: ... }`, ohne das
 * kann EmployeeData die passende Ansicht nicht wählen. Beide Felder bleiben
 * `undefined`, wenn kein Axios-Fehler vorliegt (z. B. ein reiner Netzwerkfehler
 * ohne Response).
 */
export interface FetchError extends Error {
  status?: number;
  body?: unknown;
}

function useFetch<Type>()
{
    const [data, setData] = useState<Type>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<FetchError | null>(null);

    const request = useCallback(async (url: string, method: 'get' = 'get') => {
        setLoading(true);
        setError(null);
        try
        {
            // axios >= 1.20 typisiert die Antwort als bedingten Typ
            // (`AxiosResponseResult<T, R>`), den TS bei generischem `R` nicht
            // aufloest. Der Response-Interceptor liefert `response.data`, also `Type`.
            const data = (await instance[method]<Type, Type>(url)) as Type;
            setData(data);
        }
        catch (error)
        {
            const err: FetchError = error instanceof Error ? error : new Error('Unknown error occurred');
            if (isAxiosError(error)) {
                err.status = error.response?.status;
                err.body = error.response?.data;
            }
            setError(err);
            console.error('API Error:', err.message);
            return false;
        }
        finally
        {
            setLoading(false);
        }

        return true;
    }, []);

    return { request, data, error, loading };
}

export default useFetch
